import { action, makeObservable } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Alert, Card } from 'react-bootstrap';
import { Prompt, RouteComponentProps, withRouter } from 'react-router-dom';

import Store from '../../../store';
import Error from '../../components/error/Error';
import SignalsGroups from '../../components/rules/signals/SignalsGroups';
import ScrollButton from '../../components/generic_components/ScrollButton';
import Spinner from '../../components/Spinner';

type MatchParams = {
  tenant: string;
  modelId: string;
};
interface SignalsPageProps extends RouteComponentProps<MatchParams> {
  store?: Store;
}

const SignalsPage = inject('store')(
  observer(
    class SignalsPage extends React.Component<
      SignalsPageProps,
      Record<string, never>
    > {
      constructor(props: SignalsPageProps) {
        super(props);

        makeObservable(this, {
          saveSignals: action,
        });

        this.saveSignals = this.saveSignals.bind(this);
      }

      async componentDidMount() {
        const promises = [
          this.props.store.loadValidationData({ isProcessing: true }),
        ];
        if (!this.props.store.signalsPage.savingSignals) {
          promises.push(this.props.store.loadSignals());
        }
        await Promise.all(promises);
      }

      async saveSignals() {
        this.props.store.analyticsTrackEvent('Updated signals');
        await this.props.store.saveSignals();
      }

      render() {
        const { validationPage, signalsPage } = this.props.store;
        const { signalsError, signalsSaved, loadingSignals, signals } =
          signalsPage;
        const {
          validation: { error: validationError },
        } = validationPage;

        const { tenant, modelId } = this.props.match.params;

        return (
          <Card className="mt-3 me-5 ms-5 mb-5">
            <Card.Body>
              <Prompt
                when={this.props.store.signalsPage.savingSignals}
                message="Signals are still saving. If you leave now, your changes may appear lost. Are you sure?"
              />
              <div className="mb-3">
                <p>
                  Configure the intel your Sales team sees in your CRM next to
                  the score to provide explainability and additional information
                  on the record.
                  <br />
                  Check out the result in the{' '}
                  <a
                    className="fw-bold"
                    href={`/tenant/${tenant}/models/${modelId}/review/sample`}
                  >
                    Sample
                  </a>{' '}
                  page!
                </p>

                <a
                  href="https://help.madkudu.com/docs/customer-fit-signals"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none text-primary"
                >
                  <i aria-hidden className="fas fa-book-open"></i> How to
                  configure the Customer Fit Signals
                </a>
              </div>
              {loadingSignals || !signals ? (
                <Spinner />
              ) : (
                <>
                  {validationError && <Error message={validationError} />}
                  {signalsError && (
                    <Error
                      message={signalsError.message}
                      type={signalsError.type}
                    />
                  )}

                  <SignalsGroups saveSignals={this.saveSignals} />
                  {signalsSaved && (
                    <Alert variant="success" className="mt-4">
                      Signals saved !
                    </Alert>
                  )}
                  <ScrollButton />
                </>
              )}
            </Card.Body>
          </Card>
        );
      }
    }
  )
);

export default withRouter(SignalsPage);
